import Vue from "vue";
import Vuex from "vuex";
import { Message } from "element-ui";
import {
  JOB_STATUS,
  JOB_OUTCOME,
  SYNC_RESULT,
  POLL_INTERVAL_MS,
  POLL_MAX_WAIT_MS,
  POLL_NOT_FOUND_GRACE_MS,
  POLL_REQUEST_TIMEOUT_MS,
  HTTP_UNAUTHORIZED,
  HTTP_FORBIDDEN,
  HTTP_NOT_FOUND,
  SYNC_MESSAGES
} from "../services/pccSyncJobs";

Vue.use(Vuex);

// element-ui Message duration 0 = stays until the user closes it
const UNTIL_CLOSED = 0;
// element-ui 1.4 Message is fixed at top: 20px with no per-instance offset, so sync results
// would cover each other and the top-menu "do not refresh" banner. Stack them below the menu.
const TOP_MENU_HEIGHT_PX = 60;
const SYNC_MESSAGE_GAP_PX = 8;
// wraps long sync texts (element-ui Message is one nowrap line); styled in App.vue
const SYNC_MESSAGE_CLASS = "pcc-sync-message";
const openSyncMessages = [];

const layoutSyncMessages = () => {
  let top = TOP_MENU_HEIGHT_PX + SYNC_MESSAGE_GAP_PX;
  openSyncMessages.forEach(vm => {
    vm.$el.style.top = `${top}px`;
    top += vm.$el.offsetHeight + SYNC_MESSAGE_GAP_PX;
  });
};

const showSyncMessage = options => {
  const vm = Message({
    ...options,
    // type modifier colors the stretched icon strip (App.vue)
    customClass: `${SYNC_MESSAGE_CLASS} ${SYNC_MESSAGE_CLASS}--${options.type}`,
    onClose: () => {
      openSyncMessages.splice(openSyncMessages.indexOf(vm), 1);
      layoutSyncMessages();
    }
  });
  openSyncMessages.push(vm);
  // Message shows itself (v-show) on the next tick; measuring now would give 0px
  Vue.nextTick(layoutSyncMessages);
};

export default new Vuex.Store({
  state: {
    reporting: {
      isSurveillanceReportInFlight: false
    },
    pccSync: {
      jobIds: [],
      completedCount: 0
    }
  },
  getters: {
    reportsIsBusy: state => state.reporting.isSurveillanceReportInFlight,
    pccSyncIsBusy: state => state.pccSync.jobIds.length > 0
  },
  mutations: {
    asyncReportStarted(state) {
      state.reporting.isSurveillanceReportInFlight = true;
    },
    asyncReportCompleted(state) {
      state.reporting.isSurveillanceReportInFlight = false;
    },
    pccSyncJobStarted(state, jobId) {
      state.pccSync.jobIds.push(jobId);
    },
    pccSyncJobFinished(state, jobId) {
      state.pccSync.jobIds = state.pccSync.jobIds.filter(id => id !== jobId);
    },
    pccSyncCompleted(state) {
      state.pccSync.completedCount += 1;
    }
  },
  actions: {
    initAsyncReport({ commit }, { url, payload }) {
      commit("asyncReportStarted");
      const handler = event => {
        const link = document.createElement("a");
        link.href = event.detail.message.url;
        link.download = `LineListReport.pdf`;
        link.dispatchEvent(new MouseEvent("click"));
        document.removeEventListener("message:ReportGenerated", handler);
        commit("asyncReportCompleted");
      };
      document.addEventListener("message:ReportGenerated", handler);
      Vue.http.post(url, payload).catch(() => {
        document.removeEventListener("message:ReportGenerated", handler);
      });
    },
    trackPccSyncJob({ state, commit, dispatch }, caseId) {
      if (state.pccSync.jobIds.includes(caseId)) {
        return;
      }
      commit("pccSyncJobStarted", caseId);
      const startedAt = Date.now();
      const url = `${window.CONFIG.pcc_integration_api}/async-jobs/case/${caseId}`;
      const finish = result => {
        commit("pccSyncJobFinished", caseId);
        if (result === SYNC_RESULT.SUCCESS) {
          commit("pccSyncCompleted");
        }
        dispatch("notifyPccSyncResult", result);
      };
      const resultForJob = job => {
        if (job.status === JOB_STATUS.COMPLETED) {
          if (job.outcome === JOB_OUTCOME.SYNCED) return SYNC_RESULT.SUCCESS;
          if (job.outcome === JOB_OUTCOME.SKIPPED) return SYNC_RESULT.SKIPPED;
          return SYNC_RESULT.FAILURE;
        }
        return job.status === JOB_STATUS.FAILED ? SYNC_RESULT.FAILURE : null;
      };
      const poll = async () => {
        if (Date.now() - startedAt > POLL_MAX_WAIT_MS) {
          finish(SYNC_RESULT.STILL_RUNNING);
          return;
        }
        try {
          const res = await Vue.http.get(url, { timeout: POLL_REQUEST_TIMEOUT_MS, skipAuthLogout: true });
          const result = resultForJob(res.body);
          if (result) {
            finish(result);
            return;
          }
        } catch (err) {
          const status = err && err.status;
          const withinNotFoundGrace = Date.now() - startedAt < POLL_NOT_FOUND_GRACE_MS;
          if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN ||
            (status === HTTP_NOT_FOUND && !withinNotFoundGrace)) {
            finish(SYNC_RESULT.FAILURE);
            return;
          }
          // transient error (network, timeout, 5xx) or job not created yet: keep polling
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      };
      setTimeout(poll, POLL_INTERVAL_MS);
    },
    notifyPccSyncResult(_, kind) {
      // same $message style as the rest of the app; non-success results stay until closed
      if (kind === SYNC_RESULT.SUCCESS) {
        showSyncMessage({ type: "success", message: SYNC_MESSAGES.SUCCESS });
      } else if (kind === SYNC_RESULT.SKIPPED) {
        showSyncMessage({ type: "warning", message: SYNC_MESSAGES.SKIPPED, duration: UNTIL_CLOSED, showClose: true });
      } else if (kind === SYNC_RESULT.STILL_RUNNING) {
        showSyncMessage({ type: "info", message: SYNC_MESSAGES.STILL_RUNNING, duration: UNTIL_CLOSED, showClose: true });
      } else {
        showSyncMessage({ type: "error", message: SYNC_MESSAGES.FAILURE, duration: UNTIL_CLOSED, showClose: true });
      }
    }
  }
});
